import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { ERequestType } from "../../Enum/ERequestType"
import { PlatformNotice } from "../../Objects/PlatformNotice/PlatformNotice"
import { PlatformNoticeRecipient } from "../../Objects/PlatformNotice/PlatformNoticeRecipient"
import { SavePlatformNotice } from "../../Objects/PlatformNotice/SavePlatformNotice"
import { DeleteRequest, GetRequest, PostRequest, PutRequest } from "../../services/ApiClientService"

export const usePlatformNotices = () => {
  const { t } = useTranslation(["general", "platformNotice"])

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const [isSaving, setIsSaving] = useState(false)

  const handleError = useCallback(
    (error: unknown, fallbackKey: string) => {
      const message = error instanceof Error ? error.message : t(fallbackKey)
      setError(t("general:label_title_erro"), message)
    },
    [setError, t],
  )

  const getAdminNotices = useCallback(
    async (onSuccess: (data: PlatformNotice[]) => void, silent = false) => {
      if (!silent) enableLoad()

      try {
        const response = await GetRequest<PlatformNotice[]>(
          API_ROUTES.PLATFORM_NOTICE.ADMIN_GET_ALL,
          ERequestType.Admin,
        )

        if (!response.success) throw new Error(response.message)

        onSuccess(response.data?.map((item) => new PlatformNotice(item)) ?? [])
      } catch (error) {
        handleError(error, "platformNotice:error_load")
      } finally {
        if (!silent) disableLoad()
      }
    },
    [disableLoad, enableLoad, handleError],
  )

  const getRecipients = useCallback(
    async (onSuccess: (data: PlatformNoticeRecipient[]) => void) => {
      try {
        const response = await GetRequest<PlatformNoticeRecipient[]>(
          API_ROUTES.PLATFORM_NOTICE.ADMIN_RECIPIENTS,
          ERequestType.Admin,
        )

        if (!response.success) throw new Error(response.message)

        onSuccess(response.data?.map((item) => new PlatformNoticeRecipient(item)) ?? [])
      } catch (error) {
        handleError(error, "platformNotice:error_recipients")
      }
    },
    [handleError],
  )

  const saveAdminNotice = useCallback(
    async (notice: SavePlatformNotice, id: number | undefined, onSuccess: () => void) => {
      setIsSaving(true)

      try {
        const response = id
          ? await PutRequest<unknown, SavePlatformNotice>(
              API_ROUTES.PLATFORM_NOTICE.ADMIN_UPDATE(id),
              notice,
              ERequestType.Admin,
            )
          : await PostRequest<unknown>(API_ROUTES.PLATFORM_NOTICE.ADMIN_CREATE, notice, ERequestType.Admin)

        if (!response.success) throw new Error(response.message)

        onSuccess()
      } catch (error) {
        handleError(error, "platformNotice:error_save")
      } finally {
        setIsSaving(false)
      }
    },
    [handleError],
  )

  const deleteAdminNotice = useCallback(
    async (id: number, onSuccess: () => void) => {
      try {
        const response = await DeleteRequest<unknown>(API_ROUTES.PLATFORM_NOTICE.ADMIN_DELETE(id), ERequestType.Admin)

        if (!response.success) throw new Error(response.message)

        onSuccess()
      } catch (error) {
        handleError(error, "platformNotice:error_delete")
      }
    },
    [handleError],
  )

  const getInbox = useCallback(
    async (
      onSuccess: (data: PlatformNotice[]) => void,
      silent = true,
      requestType: ERequestType = ERequestType.Store,
    ) => {
      if (!silent) enableLoad()

      try {
        const response = await GetRequest<PlatformNotice[]>(API_ROUTES.PLATFORM_NOTICE.INBOX, requestType)

        if (!response.success) throw new Error(response.message)

        onSuccess(response.data?.map((item) => new PlatformNotice(item)) ?? [])
      } catch (error) {
        handleError(error, "platformNotice:error_inbox")
      } finally {
        if (!silent) disableLoad()
      }
    },
    [disableLoad, enableLoad, handleError],
  )

  const getUnreadCount = useCallback(
    async (onSuccess: (count: number) => void, requestType = ERequestType.Store) => {
      try {
        const response = await GetRequest<number>(API_ROUTES.PLATFORM_NOTICE.UNREAD_COUNT, requestType)

        if (!response.success) throw new Error(response.message)

        onSuccess(response.data ?? 0)
      } catch {
        onSuccess(0)
      }
    },
    [],
  )

  const markAsRead = useCallback(
    async (id: number, onSuccess: () => void, requestType = ERequestType.Store) => {
      try {
        const response = await PostRequest<unknown>(API_ROUTES.PLATFORM_NOTICE.READ(id), {}, requestType)

        if (!response.success) throw new Error(response.message)

        onSuccess()
      } catch (error) {
        handleError(error, "platformNotice:error_read")
      }
    },
    [handleError],
  )

  const markAllAsRead = useCallback(
    async (onSuccess: () => void, requestType = ERequestType.Store) => {
      try {
        const response = await PostRequest<unknown>(API_ROUTES.PLATFORM_NOTICE.READ_ALL, {}, requestType)

        if (!response.success) throw new Error(response.message)

        onSuccess()
      } catch (error) {
        handleError(error, "platformNotice:error_read")
      }
    },
    [handleError],
  )

  return {
    isSaving,
    getAdminNotices,
    getRecipients,
    saveAdminNotice,
    deleteAdminNotice,
    getInbox,
    getUnreadCount,
    markAsRead,
    markAllAsRead,
  }
}
